class Roller {

  constructor(filter) {
    this.filter = filter
    this.rollBtn = document.querySelector('#rollBtn')
    this.resultsEl = document.querySelector('.results')
    this.statusEl = document.querySelector('.results__status')
    this.maritalStatusEl = document.querySelector(`.resultsGroup[data-group="marital-status"]`)
    this.childrenEl = document.querySelector(`.resultsGroup[data-group="children"]`)
    this.primaryCareerEl = document.querySelector(`.resultsGroup[data-group="primary-career"]`)
    this.secondaryCareerEl = document.querySelector(`.resultsGroup[data-group="secondary-career"]`)
    this.genGoalEl = document.querySelector(`.resultsGroup[data-group="gengoal"]`)
    this.miscFunEl = document.querySelector(`.resultsGroup[data-group="miscfun"]`)
    this.hasRolled = false
    // Bumped on every full roll, so late answers from an older roll are ignored
    this.rollId = 0
    this.descriptionId = 0
    this.descriptionVersion = 0
    this.rollAllListener()
    this.reRollListener()
  }

  // Fetch JSON from the API, adding the pack filter where it applies
  get(endpoint, useFilter = true) {
    if (useFilter && this.filter.packs.length > 0) {
      endpoint += "?exclude=" + this.filter.packs.join(',')
    }
    return fetch(endpoint).then(res => {
      if (!res.ok) throw new Error(`${endpoint} responded ${res.status}`)
      return res.json()
    })
  }

  rollAllListener() {
    this.rollBtn.addEventListener('click', async () => {
      if (this.rollBtn.disabled) return
      this.setBusy(true)
      await this.filter.close()
      const rollId = ++this.rollId
      this.emptyAllFields()
      try {
        const data = await this.get('api/roll')
        if (rollId !== this.rollId) return
        let workingSims = data.marital_status.working_sims
        this.setFields(this.maritalStatusEl, data.marital_status.title, data.marital_status.description)
        this.maritalStatusEl.setAttribute('data-workers', workingSims)
        this.setCareer(this.primaryCareerEl, data.careers.primary)
        this.setFields(this.childrenEl, data.children.children)
        this.setFields(this.genGoalEl, data.gengoal[0].title, data.gengoal[0].description)
        if (data.gengoal[0].title === "Midlife Crisis") {
          this.addMidlifeCrisis()
        }
        this.setFields(this.miscFunEl, data.miscfun[0].title, data.miscfun[0].description)
        if (data.miscfun[0].title === "Rainbow Brite") {
          this.addRainbowBrite()
        }
        this.setSecondaryCareers(data)
        this.hasRolled = true
        this.resultsEl.classList.add('results--rolled')
        this.announce(`New generation rolled: ${data.marital_status.title}, ${data.children.children.toLowerCase()}.`)
      } catch (e) {
        this.showError(e)
      } finally {
        this.setBusy(false)
      }
    })
  }

  reRollListener() {
    document.addEventListener('click', async ev => {
      const button = ev.target.closest('.resultsGroup__reroll')
      if (!button || button.disabled) return
      const group = button.getAttribute('data-reroll')
      const rollId = this.rollId
      button.disabled = true
      try {
        switch (group) {
          case 'marital-status': {
            const status = await this.get('api/maritalstatus')
            // Since number of workers probably are different, we need to re-roll careers
            const careers = await this.get(`api/careers/${status.working_sims}`)
            if (rollId !== this.rollId) return
            this.setFields(this.maritalStatusEl, status.title, status.description)
            this.maritalStatusEl.setAttribute('data-workers', status.working_sims)
            this.setCareer(this.primaryCareerEl, careers.primary)
            this.setSecondaryCareers(careers)
            this.announce(`Marital status re-rolled: ${status.title}. Careers re-rolled too.`)
            break
          }
          case 'children': {
            const data = await this.get('api/children', false)
            if (rollId !== this.rollId) return
            this.setFields(this.childrenEl, data.children)
            this.announce(`Children re-rolled: ${data.children}.`)
            break
          }
          case 'primary-career': {
            const data = await this.get('api/careers/1')
            if (rollId !== this.rollId) return
            this.setCareer(this.primaryCareerEl, data.primary)
            this.announce(`Primary career re-rolled: ${this.careerLabel(data.primary)}.`)
            break
          }
          case 'secondary-career': {
            let workers = this.maritalStatusEl.getAttribute('data-workers')
            const data = await this.get(`api/careers/${workers}`)
            if (rollId !== this.rollId) return
            this.setSecondaryCareers(data)
            this.announce('Secondary careers re-rolled.')
            break
          }
          case 'gengoal': {
            const data = await this.get('api/gengoal')
            if (rollId !== this.rollId) return
            this.setFields(this.genGoalEl, data[0].title, data[0].description)
            if (data[0].title === "Midlife Crisis") {
              this.addMidlifeCrisis()
            }
            this.announce(`Generational goal re-rolled: ${data[0].title}.`)
            break
          }
          case 'miscfun': {
            const data = await this.get('api/miscfun')
            if (rollId !== this.rollId) return
            this.setFields(this.miscFunEl, data[0].title, data[0].description)
            if (data[0].title === "Rainbow Brite") {
              this.addRainbowBrite()
            }
            this.announce(`Miscellaneous fun re-rolled: ${data[0].title}.`)
            break
          }
        }
      } catch (e) {
        this.showError(e)
      } finally {
        if (this.hasRolled && !this.rollBtn.disabled) button.disabled = false
      }
    })
  }

  // Disable Roll and all Re-roll buttons while a full roll is loading
  setBusy(busy) {
    this.rollBtn.disabled = busy
    this.resultsEl.setAttribute('aria-busy', busy ? 'true' : 'false')
    document.querySelectorAll('.resultsGroup__reroll').forEach(button => {
      button.disabled = busy || !this.hasRolled
    })
  }

  announce(message) {
    this.statusEl.classList.remove('results__status--error')
    this.statusEl.textContent = message
  }

  showError(error) {
    console.error(error)
    this.statusEl.classList.add('results__status--error')
    this.statusEl.textContent = "Couldn't roll right now. Please check your connection and try again."
  }

  emptyAllFields() {
    this.emptyFields(this.maritalStatusEl)
    this.emptyFields(this.childrenEl)
    this.emptyFields(this.primaryCareerEl)
    this.emptySecondaryCareers()
    this.emptyFields(this.genGoalEl)
    this.emptyFields(this.miscFunEl)
  }

  // Methods for emptying each group
  emptyFields(el) {
    this.removeHopperList(el)
    let result = el.querySelector('.resultsGroup__result')
    let description = el.querySelector('.resultsGroup__description')
    if (result) {
      result.innerHTML = ""
    }
    if (description) {
      description.innerHTML = ""
    }
  }

  emptySecondaryCareers() {
    let rows = this.secondaryCareerEl.querySelectorAll('.resultsGroup__row:not(.resultsGroup__row--template)')
    rows.forEach(row => {
      row.remove()
    })
  }

  // Set new values. With a description, the title becomes a button that shows/hides it
  setFields(el, title, description = null) {
    let result = el.querySelector('.resultsGroup__result')
    let descriptionEl = el.querySelector('.resultsGroup__description')
    result.innerHTML = ""
    if (description) {
      if (!descriptionEl.id) descriptionEl.id = `description-${++this.descriptionId}`
      let toggle = document.createElement('button')
      toggle.type = 'button'
      toggle.className = 'resultsGroup__toggle'
      toggle.setAttribute('aria-expanded', 'false')
      toggle.setAttribute('aria-controls', descriptionEl.id)
      toggle.textContent = title
      result.appendChild(toggle)
      descriptionEl.innerHTML = description
    } else {
      result.textContent = title || ""
      descriptionEl.innerHTML = ""
    }
    descriptionEl.style.maxHeight = "0px"
    descriptionEl.inert = true
    descriptionEl.dataset.version = ++this.descriptionVersion
  }

  // Add extra content to a description, unless it has been re-rolled in the meantime
  appendLater(descriptionEl, request, render) {
    const version = descriptionEl.dataset.version
    request.then(data => {
      if (descriptionEl.dataset.version !== version) return
      descriptionEl.insertAdjacentHTML('beforeend', render(data))
    }).catch(e => this.showError(e))
  }

  // A career roll: { type, career: [...] }. A career hopper works through all rolled careers
  setCareer(el, data) {
    this.removeHopperList(el)
    if (data.type !== 'career-hopper') {
      this.setFields(el, data.career[0].title, data.career[0].description)
      return
    }
    this.setFields(el, this.careerLabel(data))
    let list = document.createElement('ol')
    list.className = 'resultsGroup__hopper'
    data.career.forEach(career => {
      let item = document.createElement('li')
      item.innerHTML = '<h4 class="resultsGroup__result"></h4><div class="resultsGroup__description" style="max-height: 0px"></div>'
      list.appendChild(item)
      this.setFields(item, career.title, career.description)
    })
    this.rowOf(el).appendChild(list)
  }

  careerLabel(data) {
    if (data.type === 'career-hopper') return `Career Hopper: work through all ${data.career.length} careers`
    return data.career[0].title
  }

  removeHopperList(el) {
    let list = this.rowOf(el).querySelector('.resultsGroup__hopper')
    if (list) list.remove()
  }

  rowOf(el) {
    return el.classList.contains('resultsGroup__row') ? el : el.querySelector('.resultsGroup__row')
  }

  setSecondaryCareers(data) {
    this.emptySecondaryCareers()
    let secondaries = data.secondaries
    if (!secondaries && data.careers) {
      secondaries = data.careers.secondaries
    }
    if (secondaries && secondaries.length > 0) {
      if (secondaries.length === 1) {
        this.secondaryCareerEl.querySelector('.resultsGroup__title').innerHTML = "Secondary Career"
      } else {
        this.secondaryCareerEl.querySelector('.resultsGroup__title').innerHTML = "Secondary Careers"
      }
      this.secondaryCareerEl.classList.remove('resultsGroup--hidden')
      secondaries.forEach((career, i) => {
        let newEl = this.secondaryCareerEl.querySelector('.resultsGroup__row').cloneNode(true)
        newEl.classList.remove('resultsGroup__row--template')
        newEl.querySelector('.resultsGroup__description').removeAttribute('id')
        // Number the rows when there is more than one, so it's easy to tell them apart
        if (secondaries.length > 1) {
          newEl.classList.add('resultsGroup__row--indexed')
          newEl.insertAdjacentHTML('afterbegin', `<span class="resultsGroup__index"><span class="sr-only">Secondary career </span>${i + 1}</span>`)
        }
        this.secondaryCareerEl.appendChild(newEl)
        this.setCareer(newEl, career)
      })
    } else {
      this.secondaryCareerEl.classList.add('resultsGroup--hidden')
      this.emptySecondaryCareers()
    }
  }

  addMidlifeCrisis() {
    this.appendLater(this.genGoalEl.querySelector('.resultsGroup__description'), this.get("api/midlifecrisis"), data =>
      '<h3 class="resultsGroup__midlifecrisis">Your rolls:</h3>' +
      data.map(item => `<article><h4>${item.title}</h4><p>${item.description}</p></article>`).join('')
    )
  }

  addRainbowBrite() {
    this.appendLater(this.miscFunEl.querySelector('.resultsGroup__description'), this.get("api/color", false), data =>
      '<p class="resultsGroup__rainbowbrite">You rolled the color ' + data[0].title.toLowerCase() + "</p>"
    )
  }
}

export default Roller
